import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../styles/searchable-select.css';
import useIsMobile from '../hooks/useIsMobile';

const SearchableSelect = ({ options, onSelect, placeholder, value, allowCreate = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();
  const wrapperRef = useRef(null);

  const selectedOption = options.find(opt => opt.value === value)?.label || '';
  const [isNewOption, setIsNewOption] = useState(false);

  const handleClickOutside = useCallback((event) => {
    if (wrapperRef.current &&
      !wrapperRef.current.contains(event.target) &&
      !isMobile &&
      isOpen) {
      setIsOpen(false);
      setSearchTerm(selectedOption);
    }
  }, [isMobile, isOpen, selectedOption]);

  useEffect(() => {
    setSearchTerm(selectedOption);
  }, [selectedOption]);

  useEffect(() => {
    if (isOpen && !isMobile) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, isMobile, handleClickOutside]);

  const filteredOptions = [
    ...options.filter(option =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    ),
    ...(allowCreate && searchTerm.trim() && !options.some(opt => opt.label === searchTerm)
      ? [{ value: null, label: searchTerm, isNew: true }]
      : [])
  ];

  const handleSelect = (option) => {
    if (option.isNew) {
      onSelect({ value: null, label: searchTerm, isNew: true });
    } else {
      onSelect(option);
    }
    setIsOpen(false);
    setSearchTerm(option.label);
    setIsNewOption(false);
  };

  const handleMobileConfirm = () => {
    if (allowCreate && searchTerm.trim() && !filteredOptions.length) {
      handleSelect({ value: null, label: searchTerm, isNew: true });
    }
  };

  const handleClear = () => {
    setSearchTerm('');
    onSelect(null);
    setIsNewOption(false);
  };

  return (
    <div className="searchable-select" ref={wrapperRef}>
      <div className="input-with-clear">
        <input
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setIsOpen(true)}
          readOnly={isMobile}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && allowCreate && searchTerm.trim()) {
              handleSelect({ value: null, label: searchTerm, isNew: true });
              e.preventDefault();
            }
          }}
        />
        {searchTerm && (
          <button type="button" className="btn btn--ghost clear-button" onClick={handleClear}>
            ×
          </button>
        )}
      </div>

      {isOpen && !isMobile && (
        <ul className="dropdown">
          {filteredOptions.map((option) => (
            <li
              key={option.value}
              onClick={() => handleSelect(option)}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}

      {isOpen && isMobile && (
        <div className="mobile-modal">
          <div className="modal-header">
            <div className="input-with-clear">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
              {searchTerm && (
                <button type="button" className="btn btn--ghost clear-button" onClick={handleClear}>
                  ×
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={handleMobileConfirm}
              className="btn btn--success"
              disabled={!allowCreate || !searchTerm.trim()}
            >
              Confirm
            </button>

            <button type="button" onClick={() => setIsOpen(false)} className="btn btn--danger">
              Cancel
            </button>
          </div>
          <ul className="modal-list">
            {filteredOptions.map((option) => (
              <li
                key={option.value}
                onClick={() => handleSelect(option)}
              >
                {option.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;